import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { moment } from 'i18n';
import { withNamespaces } from 'react-i18next';
import { Loading } from 'components/Loading';

import { getNewOrderHistory } from 'redux/actions/orders';
import { formatNumberToPlaces } from 'utils';
import { nestedTranslate } from 'utils/strings';
import { PaginationTableWrapper } from 'containers/Tables';
// import styles from './Table.module.scss';
import { Box, Text } from 'components/Wrapped';
import { formatCrypto } from 'utils/numbers';

const UserTradeHistoryTable = ({
  t: translate,
  isAuthenticated,
  newOrderHistoryIsLoading,
  newOrderHistory,
  tradingPair: initialTradingPair,
  extraProps,
  getNewOrderHistory,
  currentPage,
  pageLimit,
  pagesAmount,
}) => {
  const [, setTradingPair] = useState(initialTradingPair || 'ALL');
  const t = nestedTranslate(translate, 'tables.tradeHistory');

  useEffect(() => {
    if (initialTradingPair) {
      setTradingPair(initialTradingPair);
    }
  }, [initialTradingPair]);

  useEffect(() => {
    if (isAuthenticated) {
      getNewOrderHistory();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const onPageChange = (pageIndex) => {
    getNewOrderHistory(pageIndex + 1, 25);
  };

  if (newOrderHistoryIsLoading) {
    return <Loading />;
  }

  return (
    <PaginationTableWrapper
      data={newOrderHistory}
      headOpacity="1"
      resizable={false}
      columns={[
        {
          Header: 'Пара',
          id: 'symbol',
          accessor: (data) => `${data.trade || ''}/${data.market || ''}`,
          Cell: (data) => (
            <Text size="12px" weight={600}>
              {data.value}
            </Text>
          ),
        },
        {
          Header: t('date'),
          accessor: 'completionDate',
          Cell: ({ value }) => {
            if (!value) return null;
            const m = moment(value);
            if (!m.isValid()) return null;
            return (
              <Box pad="none">
                <Text color="#B4B6B7" size="12px" weight={600}>
                  {m.format('DD.MM.YYYY')}
                </Text>
                <Text color="#B4B6B7" size="12px" weight={600}>
                  {m.format('HH:mm:ss')}
                </Text>
              </Box>
            );
          },
        },
        {
          Header: 'Тип',
          id: 'side',
          accessor: ({ side }) => (side ? t('orderSide', { side: side.toUpperCase() }) : ''),
          // maxWidth: 75,
          Cell: ({ value, original: { side } }) => {
            const color = side && side.toUpperCase() === 'BUY' ? '#46B08E' : '#C55445';
            return (
              <Text color={color} size="12px" weight={600}>
                {value}
              </Text>
            );
          },
        },
        {
          Header: 'Цена ордера',
          id: 'price',
          accessor: (data) => {
            const mOrders = data.mOrders || [];
            const mOrdersRates = mOrders.reduce((acc, item) => acc + (item.rate || 0), 0);

            return {
              firstValue: mOrders.length ? mOrdersRates / mOrders.length : data.rate || 0,
            };
          },
          Cell: ({ value }) => (
            <Text size="12px" weight={600}>
              {formatCrypto(value.firstValue, true)}
            </Text>
          ),
        },
        {
          Header: 'Всего',
          id: 'executed_volume',
          accessor: (data) => {
            const mOrders = data.mOrders || [];
            const executedVolume = mOrders.reduce((acc, item) => acc + (item.volume || 0), 0);

            return { executedVolume, volume: data.volume || 0, trade: data.trade || '' };
          },
          Cell: ({ value: { trade, executedVolume, volume } }) => (
            <Box pad="none">
              <Text size="12px" weight={600}>
                {formatCrypto(executedVolume, true)} {trade}
              </Text>
              <Text size="12px" weight={600}>
                {formatCrypto(volume, true)} {trade}
              </Text>
            </Box>
          ),
        },
        {
          Header: 'Сумма ордера',
          id: 'executed_amount',
          accessor: (data) => {
            const mOrders = data.mOrders || [];
            const commonAmount = mOrders.reduce((acc, item) => acc + (item.amount || 0), 0);

            return {
              market: data.market || '',
              commonAmount,
            };
          },
          Cell: ({ value: { commonAmount, market } }) => (
            <Text size="12px" weight={600}>
              {formatNumberToPlaces(commonAmount)} {market}
            </Text>
          ),
        },
        {
          Header: t('fee'),
          id: 'charge',
          accessor: (data) => {
            const mOrders = data.mOrders || [];
            return mOrders.reduce((acc, item) => acc + (item.serviceCharge || 0), 0);
          },
          Cell: ({ value, original: { side, market, trade } }) => {
            const currency = side && side.toLowerCase() === 'buy' ? trade : market;
            return `${formatNumberToPlaces(value)} ${currency || ''}`;
          },
        },
        {
          Header: 'Статус ордера',
          accessor: 'orderStatus',
          Cell: ({ value }) => {
            return (
              <Text size="12px" weight={600}>
                {value ? 'Заполнен' : 'Отменен'}
              </Text>
            );
          },
        },
        {
          Header: 'Номер ордера',
          accessor: 'orderId',
          Cell: ({ value }) => {
            if (!value) {
              return '';
            }

            const orderId = value.toString();

            return (
              <Text size="12px" weight={600}>
                {orderId.slice(0, 3) + '...' + orderId.slice(-3)}
              </Text>
            );
          },
        },
      ]}
      style={{ height: '100%' }}
      sortable={false}
      pageSize={pageLimit}
      pages={pagesAmount}
      page={currentPage - 1} // because started with 0
      onPageChange={onPageChange}
      // loading={true}
      {...extraProps}
    />
  );
};

UserTradeHistoryTable.propTypes = {
  tradingPair: PropTypes.oneOfType([
    PropTypes.shape({
      baseCurrency: PropTypes.string,
      quoteCurrency: PropTypes.string,
    }),
    PropTypes.oneOf(['ALL']),
  ]),
};

const mapStateToProps = ({ orders: { newOrderHistoryIsLoading, newOrderHistory, orderHistoryPagination }, auth }) => ({
  isAuthenticated: auth.isAuthenticated,
  newOrderHistoryIsLoading,
  newOrderHistory,
  currentPage: orderHistoryPagination.currentPage,
  pageLimit: orderHistoryPagination.limit,
  pagesAmount: orderHistoryPagination.pagesAmount,
});

const mapDispatchToProps = {
  getNewOrderHistory,
};

export default withNamespaces()(connect(mapStateToProps, mapDispatchToProps)(UserTradeHistoryTable));
